import {
  Avatar,
  Card,
  CardActionArea,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material'
import { FC, MouseEvent, useCallback, useState } from 'react'
import { useCopyToClipboard } from 'usehooks-ts'
import { IconButton, Icons } from 'utoniq-ui'

import { CollectionsSelectBoxFragment } from '@/clients/utoniq-core/schema'
import { useBrowserLink } from '@/components/lib/BrowserLink'
import { CollectionPublishedLabel } from '@/components/organisms'
import { styleSets } from '@/components/styles'
import { config } from '@/config'
import { useMessage, useRouter, useTranslation } from '@/core'
import { generateFullPath } from '@/lib/url'

type CollectionSelectBoxProps = {
  collection: CollectionsSelectBoxFragment
}
export const CollectionSelectBox: FC<CollectionSelectBoxProps> = ({
  collection,
}) => {
  const { t } = useTranslation('consoleCollections')
  const { generatePath, routerPages } = useRouter()
  const { showMessage } = useMessage()
  const [_, copy] = useCopyToClipboard()
  const { onClick } = useBrowserLink(
    generateFullPath(routerPages.CollectionDetail, {
      collectionId: collection?.id ?? '#',
    })
  )
  const thumbnailUri =
    collection.thumbnail.mediumUri ?? collection.thumbnail.uri ?? ''
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const options = [
    {
      label: t('openStoreLabel'),
      icon: (
        <ListItemIcon sx={{ '&.MuiListItemIcon-root': { minWidth: 24 } }}>
          <Icons.Window fontSize="xSmall" />
        </ListItemIcon>
      ),
      onClick() {
        onClick()
        setAnchorEl(null)
      },
    },
    {
      label: t('copyURLLabel'),
      async onClick() {
        await copy(
          generateFullPath(routerPages.CollectionDetail, {
            collectionId: collection?.id,
          })
        )
        showMessage(t('copyURLSuccess'), 'success')
        setAnchorEl(null)
      },
    },
  ]

  const handleClick = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  return (
    <Card
      elevation={0}
      sx={{ borderRadius: 0, bgcolor: 'unset', position: 'relative' }}
    >
      <Stack direction="row" spacing={3}>
        <Stack sx={{ position: 'relative' }}>
          <Avatar
            sx={{ width: 120, height: '100%', borderRadius: 2 }}
            variant="square"
            src={thumbnailUri}
          />
          {config.modules.nft && collection.nft && (
            <Stack sx={{ position: 'absolute', bottom: 4, right: 4 }}>
              <Icons.Nft fontSize="medium" filledColor />
            </Stack>
          )}
        </Stack>
        <Stack flex={1} spacing={1} sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <CollectionPublishedLabel
              status={collection.status}
              formattedStartedAt={collection.formattedStartedAt ?? ''}
              formattedEndedAt={collection.formattedEndedAt ?? ''}
            />
          </Stack>

          <Typography variant="h6" sx={{ ...styleSets.lineCamp(1) }}>
            {collection.title}
          </Typography>
          <Stack>
            <Typography variant="body2" color="text.secondary">
              {t('createdAt', { createdAt: collection.formattedCreatedAt })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('numberRegisteredItems', {
                itemCount: collection.itemCount,
              })}
            </Typography>
          </Stack>
        </Stack>
        <Stack>
          <IconButton.More
            size="xSmall"
            bgcolor="grey.100"
            color="grey.700"
            onClick={handleClick}
            sx={{
              position: 'relative',
              zIndex: 2,
              transform: 'rotate(90deg)',
            }}
          />
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            {options.map(({ icon, label, ...option }) => (
              <MenuItem {...option} sx={{ width: 200 }} key={label}>
                {icon}
                {label}
              </MenuItem>
            ))}
          </Menu>
        </Stack>
      </Stack>
      <CardActionArea
        sx={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          zIndex: 1,
        }}
        href={generatePath(routerPages.ConsoleCollectionDetail, {
          collectionId: collection.id,
        })}
      ></CardActionArea>
    </Card>
  )
}
